import React, { useEffect, useState } from 'react';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts';
import client from '../lib/feathers';

const FIELD_LABELS = {
  address: 'Address',
  location: 'Map location',
  summary: 'Summary',
  contactWebsite: 'Website',
  contactPhone: 'Phone',
  hours: 'Hours',
  socials: 'Socials',
  logo: 'Logo / image',
  categories: 'Categories',
};
const FIELD_ORDER = Object.keys(FIELD_LABELS);

function barColor(pct) {
  if (pct >= 80) return 'bg-green-500';
  if (pct >= 50) return 'bg-amber-500';
  return 'bg-red-500';
}

function CoverageBar({ label, pct }) {
  return (
    <div className="flex items-center gap-3">
      <div className="w-28 text-sm text-gray-600 shrink-0">{label}</div>
      <div className="flex-1 h-2.5 bg-gray-200 rounded">
        <div className={`h-2.5 rounded ${barColor(pct)}`} style={{ width: `${pct}%` }} />
      </div>
      <div className="w-10 text-right text-sm font-medium text-gray-800">{pct}%</div>
    </div>
  );
}

// Per-field % over time, derived from each snapshot's fieldPresent / scored.
function PerSectionTrends({ history }) {
  const rows = (history || []).filter((h) => h.fieldPresent && h.scoredListings);
  if (rows.length < 2) {
    return (
      <div className="mt-6 pt-4 border-t border-gray-100 text-xs text-gray-400">
        Per-section progress charts build as daily snapshots accumulate
        ({rows.length === 1 ? 'first data point captured' : 'no snapshots yet'}).
      </div>
    );
  }
  const keys = FIELD_ORDER.filter((k) => rows.some((r) => k in (r.fieldPresent || {})));
  const seriesFor = (k) => rows.map((r) => ({
    date: r.snapshotDate,
    pct: r.scoredListings ? Math.round(((r.fieldPresent[k] || 0) / r.scoredListings) * 100) : 0,
  }));
  return (
    <div className="mt-6 pt-4 border-t border-gray-100">
      <div className="text-sm font-medium text-gray-700 mb-3">Per-section progress over time</div>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        {keys.map((k) => {
          const s = seriesFor(k);
          const latest = s[s.length - 1].pct;
          return (
            <div key={k} className="border border-gray-200 rounded-lg p-3">
              <div className="flex items-baseline justify-between mb-1">
                <span className="text-sm text-gray-500">{FIELD_LABELS[k]}</span>
                <span className="text-lg font-bold text-gray-900">{latest}%</span>
              </div>
              <ResponsiveContainer width="100%" height={60}>
                <LineChart data={s} margin={{ top: 4, right: 4, left: 4, bottom: 0 }}>
                  <XAxis dataKey="date" hide />
                  <YAxis hide domain={[0, 100]} />
                  <Tooltip formatter={(v) => [`${v}%`, FIELD_LABELS[k]]} contentStyle={{ fontSize: 12 }} />
                  <Line type="monotone" dataKey="pct" stroke="#630801" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function DataQualityPanel() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [snapshotting, setSnapshotting] = useState(false);

  const load = async () => {
    try {
      const r = await client.service('admin/data-quality').find();
      setData(r);
      setErr(null);
    } catch (e) {
      setErr(e.message || 'Failed to load data quality');
    }
  };

  useEffect(() => { load(); }, []);

  const snapshotNow = async () => {
    setSnapshotting(true);
    try {
      await client.service('admin/data-quality').create({});
      await load();
    } catch (e) {
      // non-fatal
    } finally {
      setSnapshotting(false);
    }
  };

  if (err) {
    return (
      <div className="bg-white shadow sm:rounded-lg p-4 mb-4 text-sm text-gray-500">
        Data Quality unavailable: {err}
      </div>
    );
  }
  if (!data) {
    return (
      <div className="bg-white shadow sm:rounded-lg p-4 mb-4 text-sm text-gray-500">
        Loading Data Quality…
      </div>
    );
  }

  const { current, history } = data;
  const cov = current.fieldCoverage || {};
  const keys = FIELD_ORDER.filter((k) => k in cov);
  const trend = (history || []).map((h) => ({ date: h.snapshotDate, avg: h.avgScore }));
  const overallPct = Math.round(current.avgScore);

  return (
    <div className="bg-white shadow sm:rounded-lg p-5 mb-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">Data Quality</h2>
        <button
          type="button"
          onClick={snapshotNow}
          disabled={snapshotting}
          className="text-xs px-2 py-1 border border-gray-300 rounded text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
          title="Snapshot today's completeness now (idempotent)"
        >
          {snapshotting ? 'Snapshotting…' : 'Snapshot now'}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left: overall + per-field coverage */}
        <div>
          <div className="flex items-baseline gap-2 mb-1">
            <span className="text-4xl font-bold text-gray-900">{overallPct}</span>
            <span className="text-gray-500">/ 100 avg completeness</span>
          </div>
          <div className="text-xs text-gray-500 mb-4">
            {current.scoredListings} of {current.totalListings} active listings scored
          </div>
          <div className="space-y-2">
            {keys.map((k) => (
              <CoverageBar key={k} label={FIELD_LABELS[k]} pct={cov[k].pct} />
            ))}
          </div>
        </div>

        {/* Right: trend */}
        <div>
          <div className="text-sm font-medium text-gray-700 mb-2">Progress over time</div>
          {trend.length >= 2 ? (
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={trend} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Line type="monotone" dataKey="avg" stroke="#630801" strokeWidth={2} dot={{ r: 2 }} name="Avg completeness" />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[240px] flex items-center justify-center text-sm text-gray-400 text-center border border-dashed border-gray-200 rounded">
              The trend line builds as daily snapshots accumulate.<br />
              {trend.length === 1 ? 'First data point captured today.' : 'No snapshots yet — they run daily (or hit “Snapshot now”).'}
            </div>
          )}
        </div>
      </div>

      <PerSectionTrends history={history} />
    </div>
  );
}
